import React, { useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SEO } from "@/components/seo/SEO";
import { TrustSignalsBar } from "@/components/TrustSignalsBar";
import { RelatedServicesSection } from "@/components/RelatedServicesSection";
import { 
  CheckCircle, 
  Phone, 
  CalendarDays, 
  Zap, 
  Shield, 
  Clock, 
  Calculator,
  Home,
  Lightbulb,
  Gauge,
  Settings,
  Star,
  Award,
  ArrowRight,
  Play
} from "lucide-react";
import { Link } from "react-router-dom";
import { ElektraFormModal } from "@/components/ElektraFormModal";

// Import user-provided elektra images
const giraBanner = "/lovable-uploads/988a6a14-58dc-49f1-9611-dbc93b130352.png"; // GIRA Smart Banner
const schneiderGroepenkast = "/lovable-uploads/e66e3d6b-e6b3-41e3-940e-9f5680149582.png"; // Schneider Electric Distribution Board
const ematDistributionBoard = "/lovable-uploads/331d1708-1954-4607-b204-789e921f9b7b.png"; // EMAT Distribution Board
const eatonBreaker = "/lovable-uploads/d2cfc09d-c917-4bc8-b76d-1bbd0f12f1b2.png"; // EATON Breaker
const electricalCable = "/lovable-uploads/7a2127f2-183e-4455-bbd8-8fbe4fcef477.png"; // Electrical Cable
const wireConnectors = "/lovable-uploads/dd64902b-e458-4543-ace3-a382d8909507.png"; // Wire Connectors
const loxoneTreeCableBox = "/lovable-uploads/73c967e6-4e70-4aa0-8538-f825bb1dcbbd.png"; // Loxone Tree Cable Box
const giraSocket = "/lovable-uploads/e0370138-173f-449f-a3d3-fe25b9c1ca6f.png"; // GIRA Socket
const electricalWork = "/lovable-uploads/c43721ed-73c1-4c2b-9db9-e81d188683ea.png"; // Electrical Work

const Elektra = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedService, setSelectedService] = useState("");

  const openForm = (service: string = "") => {
    setSelectedService(service);
    setIsFormOpen(true);
  };
  // Enhanced SEO keywords for electrical services
  const primaryKeywords = [
    "elektricien nederland",
    "groepenkast vervangen", 
    "meterkast vernieuwen",
    "elektra renovatie",
    "3 fase aansluiting",
    "aardlekautomaat plaatsen",
    "perilex aanleggen",
    "led verlichting",
    "tuinverlichting",
    "smart home elektra"
  ];

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "ElectricalService",
      name: "Elektricien Nederland | Groepenkast Vervangen | SOL-VI",
      provider: {
        "@type": "Organization",
        name: "SOL-VI",
        logo: "https://sol-vi.nl/logo.png",
        address: {
          "@type": "PostalAddress",
          addressCountry: "NL",
          addressRegion: "Nederland"
        },
        contactPoint: {
          "@type": "ContactPoint",
          telephone: "+31-6-17105786",
          contactType: "customer service"
        }
      },
      areaServed: [
        "Nederland",
        "Noord-Holland", 
        "Zuid-Holland",
        "Utrecht",
        "Gelderland",
        "Noord-Brabant"
      ],
      serviceType: [
        "Groepenkast vervangen",
        "Meterkast vernieuwen",
        "3-fase aansluiting",
        "LED verlichting installatie",
        "Elektra renovatie",
        "Tuinverlichting",
        "Smart home elektra",
        "Aardlekautomaat plaatsen",
        "Perilex aanleggen",
        "Elektriciteitswerk"
      ],
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "EUR",
        description: "Gratis inspectie en vrijblijvende offerte elektra diensten"
      },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.9",
        reviewCount: "87"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Wat kost groepenkast vervangen?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "De kosten voor groepenkast vervangen variëren van €800-€1500 afhankelijk van het aantal groepen, type automaten en complexiteit. Wij geven altijd een gratis inspectie en transparante offerte."
          }
        },
        {
          "@type": "Question", 
          name: "Wanneer moet mijn meterkast vernieuwd worden?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Bij uitbreiding (warmtepomp, laadpaal, inductiekoken), verouderde zekeringen, storingen of bij renovatie is vervanging aan te raden voor veiligheid en capaciteit volgens NEN-1010 normen."
          }
        },
        {
          "@type": "Question",
          name: "Hoe lang duurt groepenkast vervangen?",
          acceptedAnswer: {
            "@type": "Answer", 
            text: "Standaard groepenkast vervangen duurt 4-8 uur. Bij verzwaring naar 3-fase of complexe installaties kan dit 1-2 dagen zijn. Wij plannen altijd minimale overlast."
          }
        },
        {
          "@type": "Question",
          name: "Wat bespaar ik met LED verlichting?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "LED verlichting bespaart 70-80% energie versus gloeilampen en 50% versus spaarlampen. Gemiddeld €200-400 per jaar voor een woning. ROI binnen 2-3 jaar."
          }
        },
        {
          "@type": "Question",
          name: "Kunnen jullie 3-fase aansluiting installeren?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Ja, wij verzorgen 3-fase aansluitingen voor warmtepompen, laadpalen en inductiekoken. Inclusief aanvraag bij netbeheerder en keuring volgens NEN-normen."
          }
        }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      name: "Loxone LED Verlichting Demonstratie",
      description: "Professionele LED verlichting installatie en smart home integratie",
      uploadDate: "2024-01-01",
      duration: "PT30S"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Elektricien Nederland | Groepenkast Vervangen | Meterkast Vernieuwen | SOL-VI"
        description="Professionele elektricien diensten: groepenkast vervangen, meterkast vernieuwen, 3-fase aansluiting, LED verlichting, tuinverlichting. Gecertificeerd, garantie, NEN-normen."
        type="article"
        jsonLd={jsonLd}
      />
      <Header />
      <main>
        {/* Professional Hero Section with GIRA Banner */}
        <section className="relative pt-20 pb-16 overflow-hidden">
          {/* GIRA Banner Background */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${giraBanner})`,
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/85 to-background/90"></div>
          </div>
          
          <div className="relative container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-left">
                <div className="flex items-center gap-2 mb-4">
                  <Badge variant="secondary" className="bg-primary/20 text-primary border-primary/30">
                    <Zap className="h-3 w-3 mr-1" />
                    24/7 Spoeddienst
                  </Badge>
                  <Badge variant="outline" className="border-primary/30">
                    <Shield className="h-3 w-3 mr-1" />
                    NEN-1010 Gecertificeerd
                  </Badge>
                </div>
                
                <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                  Professionele Elektricien
                  <span className="block bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                    Nederland
                  </span>
                </h1>
                
                <p className="text-xl text-foreground/80 mb-8 max-w-xl">
                  Van verouderde zekeringen naar moderne smart switches. Groepenkast vervangen, 3-fase aansluiting en intelligente verlichting door gecertificeerde elektriciens.
                </p>
                
                {/* Enhanced Trust Indicators */}
                <div className="flex flex-wrap items-center gap-6 mb-8">
                  <div className="flex items-center gap-2">
                    <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                    <span className="font-semibold text-foreground">4.9/5</span>
                    <span className="text-muted-foreground">(87 reviews)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Award className="h-5 w-5 text-primary" />
                    <span className="font-semibold text-foreground">15+ jaar ervaring</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-green-600" />
                    <span className="font-semibold text-foreground">Binnen 24u</span>
                  </div>
                </div>

                {/* Premium CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <Button 
                    variant="cta" 
                    size="lg"
                    onClick={() => openForm()} 
                    className="text-lg px-8 py-6 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  >
                    <Calculator className="mr-2 h-5 w-5" />
                    Gratis Inspectie & Offerte
                  </Button>
                  <Button 
                    variant="outline" 
                    size="lg"
                    onClick={() => openForm("spoedstoring")}
                    className="text-lg px-8 py-6 border-primary/50 hover:border-primary hover:bg-primary/5"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    24/7 Spoeddienst
                  </Button>
                </div>

                {/* Partnership Badges */}
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-sm font-medium text-foreground">Wij werken met merken als:</span>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="text-xs">GIRA</Badge>
                    <Badge variant="outline" className="text-xs">Schneider</Badge>
                    <Badge variant="outline" className="text-xs">EATON</Badge>
                    <Badge variant="outline" className="text-xs">Loxone</Badge>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground">
                  ✓ Gratis inspectie ter waarde van €75 • ✓ Binnen 24 uur reactie • ✓ Vaste prijzen, geen verrassingen
                </p>
              </div>

              {/* Loxone Video Showcase */}
              <div className="relative">
                <div className="bg-gradient-to-br from-card/50 to-accent/20 rounded-2xl p-6 backdrop-blur-sm border border-border/50">
                  <div className="aspect-video rounded-xl overflow-hidden bg-muted/30 relative group">
                    <video 
                      autoPlay 
                      muted 
                      loop 
                      playsInline
                      className="w-full h-full object-cover"
                      poster={schneiderGroepenkast}
                    >
                      <source 
                        src="https://pim.loxone.com//01%20Product%20Data/01%20Products/Basics%20%26%20Accessories/100498%20-%20Pr%C3%A4senzmelder%20Deckeneinbau%20Tree%20Gold/Images/vi-overview-100498-flushmounted-presence-sensor-tree-gold.webm" 
                        type="video/webm" 
                      />
                    </video>
                    <div className="absolute inset-0 bg-gradient-to-t from-background/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <p className="text-white text-sm font-medium bg-background/20 backdrop-blur-sm rounded px-3 py-1">
                        Smart Home Aanwezigheidssensor
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <p className="text-lg font-semibold text-foreground mb-1">Intelligente Elektra Systemen</p>
                    <p className="text-muted-foreground text-sm">Loxone Tree technologie voor moderne woningen</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Signals Bar */}
        <TrustSignalsBar />

        {/* Enhanced Service Cards Grid */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Elektricien Diensten Nederland</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Van groepenkast vervangen tot complete elektra renovaties. Gecertificeerd, betrouwbaar en met garantie.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Groepenkast Vervangen Card */}
              <Card className="group hover:shadow-xl transition-all duration-300 overflow-hidden">
                <div className="aspect-video bg-muted overflow-hidden relative">
                  <img 
                    src={schneiderGroepenkast} 
                    alt="Schneider Electric Groepenkast - Moderne elektra installatie"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 right-4">
                    <Badge variant="secondary" className="bg-background/90 backdrop-blur-sm">NEN-1010</Badge>
                  </div>
                  <div className="absolute bottom-4 left-4">
                    <Badge variant="outline" className="bg-background/90 backdrop-blur-sm border-primary">
                      Schneider Electric
                    </Badge>
                  </div>
                </div>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl group-hover:text-primary transition-colors">
                      Groepenkast Vervangen
                    </CardTitle>
                    <Badge variant="outline">€800-€1500</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Moderne groepenkast met aardlekautomaten voor optimale veiligheid en capaciteit.
                  </p>
                  <ul className="space-y-2 text-sm mb-4">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Aardlekautomaten vervangen
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      3-fase verzwaring mogelijk
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      NEN-keuring inclusief
                    </li>
                  </ul>
                  <Button variant="outline" className="w-full" onClick={() => openForm("groepenkast")}>
                    Gratis Inspectie
                  </Button>
                </CardContent>
              </Card>

              {/* LED Verlichting Card with Video */}
              <Card className="group hover:shadow-xl transition-all duration-300 overflow-hidden">
                <div className="aspect-video bg-muted overflow-hidden">
                  <video 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                  >
                    <source src="https://pim.loxone.com//01%20Product%20Data/01%20Products/Loxone%20Config/LED/100420%20-%20LED%20Spot%20RGBW%20Tree%20antraciet/Images/vi-overview-100420-led-spot-rgbw-tree-antraciet.webm" type="video/webm" />
                    LED verlichting demonstratie
                  </video>
                </div>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl group-hover:text-primary transition-colors">
                      LED Verlichting & Smart Home
                    </CardTitle>
                    <Badge variant="secondary">
                      <Lightbulb className="h-3 w-3 mr-1" />
                      80% Besparing
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Energiezuinige LED met smart home integratie. Loxone partner voor complete automatisering.
                  </p>
                  <ul className="space-y-2 text-sm mb-4">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      RGBW LED spots & strips
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Automatische schakelingen
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      App bediening & scenes
                    </li>
                  </ul>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" size="sm" onClick={() => openForm("led-verlichting")}>
                      LED Offerte
                    </Button>
                    <Link to="/smarthome">
                      <Button variant="secondary" size="sm" className="w-full">
                        Smart Home
                        <ArrowRight className="h-3 w-3 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>

              {/* 3-Fase Aansluiting Card */}
              <Card className="group hover:shadow-xl transition-all duration-300 overflow-hidden">
                <div className="aspect-video bg-gradient-to-br from-accent/10 to-primary/10 relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <Zap className="h-16 w-16 text-primary mx-auto mb-2" />
                      <Badge variant="secondary">380V</Badge>
                    </div>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-xl group-hover:text-primary transition-colors">
                    3-Fase Aansluiting
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Voor warmtepomp, laadpaal en inductiekoken. Inclusief netbeheerder aanvraag.
                  </p>
                  <ul className="space-y-2 text-sm mb-4">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Warmtepomp geschikt
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Laadpaal aansluiting
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Aanvraag netbeheerder
                    </li>
                  </ul>
                  <Button variant="outline" className="w-full" onClick={() => openForm("3-fase")}>
                    3-Fase Advies
                  </Button>
                </CardContent>
              </Card>

              {/* Tuinverlichting Card */}
              <Card className="group hover:shadow-xl transition-all duration-300 overflow-hidden">
                <div className="aspect-video bg-muted overflow-hidden relative">
                  <img 
                    src={loxoneTreeCableBox} 
                    alt="Loxone Tree Cable Box - Smart garden verlichting bekabeling"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 right-4">
                    <Badge variant="secondary" className="bg-background/90 backdrop-blur-sm">IP65</Badge>
                  </div>
                  <div className="absolute bottom-4 left-4">
                    <Badge variant="outline" className="bg-background/90 backdrop-blur-sm border-primary">
                      <Home className="h-3 w-3 mr-1" />
                      Loxone Tree
                    </Badge>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-xl group-hover:text-primary transition-colors">
                    Tuin & Buitenverlichting
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Sfeervolle en veilige buitenverlichting met automatische bediening.
                  </p>
                  <ul className="space-y-2 text-sm mb-4">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Pad & spotverlichting
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Grondkabels & verdelers
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Bewegingsdetectie
                    </li>
                  </ul>
                  <Button variant="outline" className="w-full" onClick={() => openForm("tuinverlichting")}>
                    Tuinverlichting Plan
                  </Button>
                </CardContent>
              </Card>

              {/* Elektra Renovatie Card */}
              <Card className="group hover:shadow-xl transition-all duration-300 overflow-hidden">
                <div className="aspect-video bg-muted overflow-hidden relative">
                  <img 
                    src={electricalCable} 
                    alt="Elektrische kabel met gekleurde draden - Professionele bekabeling"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 right-4">
                    <Badge variant="secondary" className="bg-background/90 backdrop-blur-sm">Renovatie</Badge>
                  </div>
                  <div className="absolute bottom-4 left-4">
                    <Badge variant="outline" className="bg-background/90 backdrop-blur-sm border-primary">
                      <Settings className="h-3 w-3 mr-1" />
                      Kabels & Installatie
                    </Badge>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-xl group-hover:text-primary transition-colors">
                    Elektra Renovatie
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Complete elektrische installatie vernieuwen voor nieuwbouw of renovatie.
                  </p>
                  <ul className="space-y-2 text-sm mb-4">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Nieuwe bekabeling
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Stopcontacten & schakelaars
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      NEN-keuring & certificaat
                    </li>
                  </ul>
                  <Button variant="outline" className="w-full" onClick={() => openForm("renovatie")}>
                    Renovatie Offerte
                  </Button>
                </CardContent>
              </Card>

              {/* Smart Home Integration Card with Video */}
              <Card className="group hover:shadow-xl transition-all duration-300 overflow-hidden">
                <div className="aspect-video bg-muted overflow-hidden">
                  <video 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                  >
                    <source src="https://pim.loxone.com//01%20Product%20Data/01%20Products/Loxone%20Config/LED/100424%20-%20LED%20Ceiling%20Light%20RGBW%20Air/Images/vi-overview-100424-led-ceiling-light-rgbw-air.webm" type="video/webm" />
                    Smart home integratie demonstratie
                  </video>
                </div>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl group-hover:text-primary transition-colors">
                      Smart Home Elektra
                    </CardTitle>
                    <Badge variant="secondary">
                      <Settings className="h-3 w-3 mr-1" />
                      Loxone Partner
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Complete smart home automatisering met Loxone. Van verlichting tot klimaat.
                  </p>
                  <ul className="space-y-2 text-sm mb-4">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Automatische schakelingen
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      App bediening overal
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Energieoptimalisatie
                    </li>
                  </ul>
                  <Link to="/smarthome" className="block">
                    <Button variant="cta" className="w-full">
                      Ontdek Smart Home
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Enhanced Technical Deep Dive Section */}
        <section className="py-16 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-3 gap-12">
              <div className="lg:col-span-2 space-y-12">
                {/* Groepenkast Guide */}
                <article>
                  <h2 className="text-3xl font-bold mb-6">Complete Gids: Groepenkast Vervangen Nederland</h2>
                  <div className="prose prose-lg max-w-none">
                    <h3 className="text-xl font-semibold mb-4 text-primary">Wanneer groepenkast vervangen?</h3>
                    <p className="text-muted-foreground mb-6">
                      Een groepenkast vervangen is noodzakelijk bij uitbreiding van uw elektrische installatie, verouderde componenten, of bij renovatie. Moderne eisen zoals warmtepompen, laadpalen en inductiekoken vragen om verzwaring naar 3-fase.
                    </p>
                    
                    <div className="grid md:grid-cols-2 gap-6 mb-8">
                      <div className="space-y-4">
                        <h4 className="font-semibold">Signalen voor vervanging:</h4>
                        <ul className="space-y-2">
                          {[
                            "Smeltzekeringen ipv aardlekautomaten",
                            "Storingen en spanningspieken",
                            "Onvoldoende groepen voor apparaten",
                            "Geen 3-fase voor warmtepomp/laadpaal"
                          ].map((item) => (
                            <li key={item} className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-red-500" />
                              <span className="text-sm">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="space-y-4">
                        <h4 className="font-semibold">Moderne voordelen:</h4>
                        <ul className="space-y-2">
                          {[
                            "Aardlekautomaten voor veiligheid",
                            "Meer vermogen en capaciteit",
                            "Geschikt voor moderne apparaten",
                            "NEN-1010 conform en gekeurd"
                          ].map((item) => (
                            <li key={item} className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-green-500" />
                              <span className="text-sm">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                     <h3 className="text-xl font-semibold mb-4 text-primary">Kosten Groepenkast Vervangen 2024</h3>
                    <div className="bg-background rounded-lg p-6 border">
                      <div className="grid md:grid-cols-3 gap-4 text-center">
                        <div>
                          <h4 className="font-semibold mb-2">Basis Vervanging</h4>
                          <p className="text-2xl font-bold text-primary">€800-€1200</p>
                          <p className="text-sm text-muted-foreground">6-12 groepen, standaard</p>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">3-Fase Verzwaring</h4>
                          <p className="text-2xl font-bold text-primary">€1200-€1800</p>
                          <p className="text-sm text-muted-foreground">Warmtepomp geschikt</p>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">Complete Renovatie</h4>
                          <p className="text-2xl font-bold text-primary">€1500-€2500</p>
                          <p className="text-sm text-muted-foreground">Nieuwe bekabeling</p>
                        </div>
                      </div>
                    </div>

                     <h3 className="text-xl font-semibold mb-4 text-primary mt-8">Professionele Componenten & Techniek</h3>
                     <div className="grid md:grid-cols-2 gap-6 mb-8">
                       <div className="bg-background rounded-lg p-6 border">
                         <div className="flex items-center gap-4 mb-4">
                             <img 
                               src={wireConnectors} 
                               alt="Wire connectors - Professionele elektra verbindingen"
                               className="w-16 h-16 object-cover rounded"
                             />
                           <div>
                             <h4 className="font-semibold">Terminal Blocks</h4>
                             <p className="text-sm text-muted-foreground">Betrouwbare verbindingen</p>
                           </div>
                         </div>
                         <p className="text-sm text-muted-foreground">
                           Hoogwaardige terminal blocks voor veilige en duurzame elektra verbindingen in uw groepenkast.
                         </p>
                       </div>
                       
                        <div className="bg-background rounded-lg p-6 border">
                          <div className="flex items-center gap-4 mb-4">
                              <img 
                                src={eatonBreaker} 
                                alt="EATON aardlekautomaat - Moderne elektra beveiliging"
                                className="w-16 h-16 object-cover rounded"
                              />
                            <div>
                              <h4 className="font-semibold">EATON Aardlekautomaten</h4>
                              <p className="text-sm text-muted-foreground">Premium beveiliging</p>
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            EATON aardlekautomaten voor optimale beveiliging en veiligheid in moderne groepenkast installaties.
                          </p>
                        </div>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-6 mb-8">
                        <div className="bg-background rounded-lg p-6 border">
                          <div className="flex items-center gap-4 mb-4">
                              <img 
                                src={giraSocket} 
                                alt="GIRA stopcontact met USB - Smart charging stations"
                                className="w-16 h-16 object-cover rounded"
                              />
                            <div>
                              <h4 className="font-semibold">GIRA Stopcontacten</h4>
                              <p className="text-sm text-muted-foreground">Smart charging stations</p>
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Moderne GIRA stopcontacten met USB en smart functies voor toekomstbestendige elektra installaties.
                          </p>
                        </div>
                        
                        <div className="bg-background rounded-lg p-6 border">
                          <div className="flex items-center gap-4 mb-4">
                              <img 
                                src={electricalWork} 
                                alt="Professioneel elektriciteitswerk - Montage en bekabeling"
                                className="w-16 h-16 object-cover rounded"
                              />
                            <div>
                              <h4 className="font-semibold">Vakmanschap</h4>
                              <p className="text-sm text-muted-foreground">15+ jaar ervaring</p>
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Professionele montage en bekabeling door gecertificeerde elektriciens volgens NEN-1010 normen.
                          </p>
                        </div>
                     </div>
                  </div>
                </article>

                {/* Smart LED Section */}
                <article>
                  <h2 className="text-3xl font-bold mb-6">LED Verlichting & Smart Home Integratie</h2>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-xl font-semibold mb-4">Energiebesparing LED</h3>
                      <div className="bg-background rounded-lg p-6 border">
                        <div className="space-y-4">
                          <div className="flex justify-between">
                            <span>Halogeen 50W</span>
                            <span className="font-semibold">→ LED 6W</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Besparing per jaar</span>
                            <span className="font-semibold text-green-600">€35 per lamp</span>
                          </div>
                          <div className="flex justify-between">
                            <span>ROI periode</span>
                            <span className="font-semibold">2-3 jaar</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-semibold mb-4">Smart Home Voordelen</h3>
                      <ul className="space-y-3">
                        {[
                          "Automatische aan/uit op aanwezigheid",
                          "Dimbare RGBW kleuren per scene",
                          "App bediening vanuit bed of kantoor",
                          "Integratie met Loxone systeem"
                        ].map((item) => (
                          <li key={item} className="flex items-center gap-3">
                            <Lightbulb className="h-5 w-5 text-yellow-500" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-6">
                        <Link to="/smarthome">
                          <Button variant="outline" className="w-full">
                            Bekijk Smart Home Pagina
                            <ArrowRight className="h-4 w-4 ml-2" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </article>

                {/* Enhanced FAQ */}
                <article>
                  <h2 className="text-3xl font-bold mb-6">Veelgestelde Vragen Elektricien</h2>
                  <div className="space-y-4">
                    {[
                      {
                        q: "Wat kost groepenkast vervangen in 2024?",
                        a: "Kosten variëren van €800-€1500 voor standaard vervanging tot €1800-€2500 voor 3-fase verzwaring. Inclusief materialen, arbeid en NEN-keuring. Gratis inspectie bepaalt exacte kosten."
                      },
                      {
                        q: "Hoe lang duurt groepenkast vervangen?",
                        a: "Standaard groepenkast vervangen duurt 4-8 uur. 3-fase verzwaring of complexe installaties 1-2 dagen. Wij plannen werkzaamheden met minimale overlast."
                      },
                      {
                        q: "Moet ik naar 3-fase voor warmtepomp?",
                        a: "Voor warmtepompen >6kW is 3-fase aan te raden voor optimale prestaties en stabiliteit. Ook voor laadpalen en inductiekoken. Wij adviseren op maat en regelen netbeheerder aanvraag."
                      },
                      {
                        q: "Wat bespaar ik met LED verlichting?",
                        a: "LED bespaart 70-80% energie vs gloeilampen, 50% vs spaarlampen. Gemiddeld €200-400/jaar voor een woning. Met smart home automatisering nog meer besparing."
                      },
                      {
                        q: "Kunnen jullie ook tuinverlichting installeren?",
                        a: "Ja, wij leggen complete buiteninstallaties aan: grondkabels, IP65+ armaturen, bewegingsmelders en automatische schakelingen. Veilig en conform normen."
                      }
                    ].map((faq, idx) => (
                      <details key={idx} className="rounded-lg border border-border p-6 bg-background">
                        <summary className="font-semibold cursor-pointer text-lg hover:text-primary transition-colors">
                          {faq.q}
                        </summary>
                        <p className="mt-4 text-muted-foreground leading-relaxed">{faq.a}</p>
                      </details>
                    ))}
                  </div>
                </article>
              </div>

              {/* Enhanced Sidebar */}
              <aside className="space-y-8">
                {/* Spoedstoring Contact */}
                <Card className="bg-red-50 border-red-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="h-5 w-5 text-red-600" />
                      <h3 className="text-lg font-bold text-red-800">Spoedstoring?</h3>
                    </div>
                    <p className="text-red-700 text-sm">24/7 bereikbaar voor noodgevallen</p>
                  </CardHeader>
                  <CardContent>
                    <a 
                      href="tel:0617105786" 
                      className="flex items-center justify-center gap-2 bg-red-600 text-white rounded-lg py-3 px-4 font-semibold hover:bg-red-700 transition-colors"
                    >
                      <Phone className="h-4 w-4" /> 
                      06 17 10 57 86
                    </a>
                    <p className="text-xs text-red-600 mt-2 text-center">Binnen 2 uur ter plaatse</p>
                  </CardContent>
                </Card>

                {/* Gratis Inspectie */}
                <Card className="bg-gradient-to-br from-primary/5 to-secondary/5">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Calculator className="h-5 w-5 text-primary" />
                      <h3 className="text-lg font-bold">Gratis Inspectie</h3>
                    </div>
                    <p className="text-muted-foreground text-sm">
                      Professionele groepenkast inspectie ter waarde van €75
                    </p>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-sm mb-4">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-green-600" />
                        <span>Veiligheidscontrole</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-green-600" />
                        <span>Capaciteitsanalyse</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-green-600" />
                        <span>Offerte op maat</span>
                      </li>
                    </ul>
                    <Button 
                      variant="cta" 
                      className="w-full" 
                      onClick={() => openForm()}
                    >
                      Plan Inspectie
                    </Button>
                  </CardContent>
                </Card>

                {/* Smart Home CTA */}
                <Card className="bg-gradient-to-br from-accent/5 to-primary/5">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Settings className="h-5 w-5 text-accent" />
                      <h3 className="text-lg font-bold">Smart Home Upgrade</h3>
                    </div>
                    <p className="text-muted-foreground text-sm">
                      Combineer elektra met slimme automatisering
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center gap-2 text-sm">
                        <Lightbulb className="h-4 w-4 text-yellow-500" />
                        <span>LED + automatisering</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Home className="h-4 w-4 text-blue-500" />
                        <span>Complete woningcontrole</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Gauge className="h-4 w-4 text-green-500" />
                        <span>Energieoptimalisatie</span>
                      </div>
                    </div>
                    <Link to="/smarthome" className="block">
                      <Button variant="outline" className="w-full">
                        Ontdek Smart Home
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>

                {/* Adviesgesprek Booking */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <CalendarDays className="h-5 w-5 text-primary" />
                      <h3 className="text-lg font-bold">Plan Adviesgesprek</h3>
                    </div>
                    <p className="text-muted-foreground text-sm">
                      Vrijblijvend advies op locatie of videobellen
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 mb-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Clock className="h-3 w-3 text-green-600" />
                        <span>30 minuten</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-green-600" />
                        <span>Geheel kosteloos</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Shield className="h-3 w-3 text-green-600" />
                        <span>Geen verplichtingen</span>
                      </div>
                    </div>
                    <Button 
                      variant="hero" 
                      className="w-full" 
                      onClick={() => (window as any).Calendly.initPopupWidget({url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800'})}
                    >
                      <CalendarDays className="h-4 w-4 mr-2" /> 
                      Plan Afspraak
                    </Button>
                  </CardContent>
                </Card>

                {/* Trust Badges */}
                <Card className="text-center">
                  <CardContent className="p-4">
                    <h4 className="font-semibold mb-3">Gecertificeerd & Betrouwbaar</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-center gap-2">
                        <Shield className="h-4 w-4 text-green-600" />
                        <span className="text-sm">NEN-1010 Gecertificeerd</span>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <Award className="h-4 w-4 text-blue-600" />
                        <span className="text-sm">15+ jaar ervaring</span>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <Star className="h-4 w-4 text-yellow-500" />
                        <span className="text-sm">4.9/5 sterren (87 reviews)</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </aside>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <RelatedServicesSection 
          currentService="elektra"
          title="Gerelateerde Duurzame Oplossingen"
          description="Maak uw huis compleet energiezuinig en duurzaam"
        />

        {/* Final CTA Section */}
        <section className="py-16 bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Klaar voor Moderne Elektra?</h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Van groepenkast vervangen tot smart home automatisering. Gecertificeerd, betrouwbaar en met garantie.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                variant="cta" 
                size="lg"
                onClick={() => openForm()} 
                className="text-lg px-8 py-6"
              >
                <Calculator className="mr-2 h-5 w-5" />
                Start Gratis Inspectie
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => (window as any).Calendly.initPopupWidget({url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800'})}
                className="text-lg px-8 py-6"
              >
                <CalendarDays className="mr-2 h-5 w-5" />
                Plan Adviesgesprek
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              ✓ Binnen 24 uur reactie • ✓ Gratis inspectie ter waarde van €75 • ✓ NEN-1010 gecertificeerd
            </p>
          </div>
        </section>
      </main>
      <Footer />
      
      <ElektraFormModal 
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        defaultService={selectedService}
      />
    </div>
  );
};

export default Elektra;
